import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';
import { ApiPageResponse, PageQuery } from '../types/api.types';
import {
  OwnerType,
  PatchVehicleOwnerRequest,
  RegisterVehicleOwnerRequest,
  VehicleOwner,
} from '../types/owner.types';

@Injectable({ providedIn: 'root' })
export class OwnersService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.gatewayUrl}/rental/api/vehicle-owners`;

  // ── Self-service (current user) ────────────────────────────────────────

  /** Devenir propriétaire — `POST /register`. Le back assigne le rôle
   *  `VEHICLE_OWNER` puis le front doit refresh le JWT pour le récupérer. */
  register(body: RegisterVehicleOwnerRequest): Observable<VehicleOwner> {
    return this.http.post<VehicleOwner>(`${this.base}/register`, body);
  }

  /** Profil owner du user courant (404 si pas encore enregistré). */
  me(): Observable<VehicleOwner> {
    return this.http.get<VehicleOwner>(`${this.base}/me`);
  }

  /** Patch partiel du profil owner courant. */
  updateMe(patch: PatchVehicleOwnerRequest): Observable<VehicleOwner> {
    return this.http.patch<VehicleOwner>(`${this.base}/me`, patch);
  }

  /** Switch INDIVIDUAL ↔ COMPANY — endpoint dédié côté back. */
  updateOwnerType(ownerType: OwnerType): Observable<VehicleOwner> {
    return this.http.post<VehicleOwner>(`${this.base}/me/owner-type`, { ownerType });
  }

  // ── Admin reads (gardés pour compat — utilisés par la fiche owner si admin) ─

  list(query: PageQuery = {}): Observable<ApiPageResponse<VehicleOwner>> {
    let params = new HttpParams()
      .set('pageable', 'true')
      .set('page', String(query.page ?? 1))
      .set('size', String(query.size ?? 10))
      .set('isDeleted', String(query.isDeleted ?? false));
    if (query.search?.trim()) {
      params = params.set('search', query.search.trim());
    }
    return this.http.get<ApiPageResponse<VehicleOwner>>(this.base, { params });
  }

  get(id: number): Observable<VehicleOwner> {
    return this.http.get<VehicleOwner>(`${this.base}/${id}`);
  }

  getByUserId(userId: number): Observable<VehicleOwner> {
    return this.http.get<VehicleOwner>(`${this.base}/user/${userId}`);
  }
}
